import React from 'react';
import { MapIcon, DocumentArrowDownIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { S3Image } from '../../lib/s3';
import { FormattedMessage } from 'react-intl';
import { pctSymbol } from '../../utils/formatters';

const SuccessionPlanningTile = ({ successionPlanningData }) => {
  // Provide defaults if successionPlanningData is undefined
  const safeData = successionPlanningData || {};
  
  // Status can be 'not_started', 'in_progress', or 'completed'
  const status = safeData?.status || 'not_started';
  const completionPercentage = safeData?.completionPercentage || 0;
  const currentSection = safeData?.currentSection || '';
  
  // Status-based CSS classes (matching dashboard pattern)
  const getStatusClasses = () => {
    switch (status) {
      case 'completed':
        return { 
          textColor: 'text-succession-success', 
          bgColor: 'bg-succession-success-bg',
          progressColor: 'bg-succession-success'
        };
      case 'in_progress':
        return { 
          textColor: 'text-succession-warning', 
          bgColor: 'bg-succession-warning-bg',
          progressColor: 'bg-succession-warning'
        };
      default:
        return { 
          textColor: 'text-succession-error', 
          bgColor: 'bg-succession-error-bg',
          progressColor: 'bg-succession-error'
        };
    }
  };

  const statusClasses = getStatusClasses();
  const showWarning = status === 'not_started' || completionPercentage === 0;
  
  // Navigate to plans page for download - completed plans show export options there
  const handleDownloadClick = (e) => {
    e.preventDefault();
    window.location.href = '/succession/plans';
  };
  
  return (
    <div className="bg-succession-bg-primary shadow-succession-md rounded-lg p-6 border border-succession-border transition-colors duration-300">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-medium text-succession-text-primary transition-colors duration-300">
          <FormattedMessage id="succession.planning_tile.title" />
        </h3>
        <MapIcon className="h-6 w-6 text-succession-primary transition-colors duration-300" />
      </div>

      <div className="space-y-4">
        {showWarning ? (
          <>
            <div>
              <span className="text-xs font-semibold inline-block py-2 px-3 rounded-md text-succession-error bg-succession-error-bg transition-colors duration-300">
                <FormattedMessage id="succession.planning_tile.warning" />
              </span>
            </div>
            <div className="flex items-center justify-center h-28 mb-2">
              <img src={S3Image('no_succession_plan.png')} alt="No succession plan" className="w-auto object-contain" style={{ height: '7.5rem' }} />
            </div>
          </>
        ) : (
          <>
            {/* Status Badge */}
            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses.bgColor} ${statusClasses.textColor} transition-colors duration-300`}>
              {status === 'completed' ? <FormattedMessage id="succession.planning_tile.status_completed" /> :
               status === 'in_progress' ? <FormattedMessage id="succession.planning_tile.status_in_progress" /> : <FormattedMessage id="succession.planning_tile.status_not_started" />}
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-succession-text-secondary transition-colors duration-300"><FormattedMessage id="succession.planning_tile.progress_label" /></span>
                <span className="text-succession-text-primary font-medium transition-colors duration-300">{completionPercentage}{pctSymbol()}</span>
              </div>
              <div className="w-full bg-succession-bg-tertiary rounded-full h-2 transition-colors duration-300">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${statusClasses.progressColor}`}
                  style={{ width: `${completionPercentage}%` }}
                ></div>
              </div>
            </div>
          </>
        )}

        {/* Current Section (only show if in progress) */}
        {status === 'in_progress' && currentSection && (
          <p className="text-sm text-succession-text-secondary transition-colors duration-300">
            <FormattedMessage id="succession.planning_tile.current_section" /> <span className="font-medium text-succession-text-primary">{currentSection}</span>
          </p>
        )}

        {/* Description */}
        {status !== 'not_started' && (
          <p className="text-sm text-succession-text-secondary leading-relaxed transition-colors duration-300">
            {status === 'completed'
              ? <FormattedMessage id="succession.planning_tile.description_completed" />
              : <FormattedMessage id="succession.planning_tile.description_in_progress" />}
          </p>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          {status === 'completed' ? (
            <>
              <button
                onClick={handleDownloadClick}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-succession-primary border border-transparent rounded-md hover:bg-succession-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary transition-all duration-300"
              >
                <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                <FormattedMessage id="succession.planning_tile.download_plan" />
              </button>
              <a
                href="/succession/succession_planning"
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-succession-primary bg-succession-bg-primary border border-succession-border rounded-md hover:bg-succession-bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary transition-all duration-300"
              >
                <FormattedMessage id="succession.planning_tile.review_plan" />
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </a>
            </>
          ) : (
            <a
              href="/succession/succession_planning"
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-succession-primary border border-transparent rounded-md hover:bg-succession-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary transition-all duration-300"
            >
              {status === 'in_progress' ? <FormattedMessage id="succession.planning_tile.continue_planning" /> : <FormattedMessage id="succession.planning_tile.start_planning" />}
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuccessionPlanningTile; 